import { useMemo } from "react";
import {
  BadgeCheck,
  CheckCircle,
  ChevronRight,
  ExternalLink,
  Gift,
  Heart,
  ShieldCheck,
  Sparkles,
  Star,
  Undo2,
  WalletCards,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProductDetailAccordion from "./ProductDetailAccordion";
import {
  buildRefProductDetailContent,
} from "./productDetailContent";
import { formatPrice, RefProductDetailData } from "../RefProductDetailTypes";
import "./ProductDetailCenter.css";

interface Props {
  product: RefProductDetailData;
}

const ProductDetailCenter = ({ product }: Props) => {
  const content = useMemo(
    () => buildRefProductDetailContent(product),
    [product]
  );
  const navigate = useNavigate();

  const brandName =
    typeof product.brand === "string" && product.brand.trim()
      ? product.brand.trim()
      : "Salar Store";

  const brandSlug =
    product.sellerSlug ||
    brandName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const discount = product.discountPercent;
  const saving = product.savingsAmount;

  return (
    <section className="pd-center">
      <div className="pd-primary">
        <h1>{product.title}</h1>

        {product.description && (
          <p className="pd-description">{product.description}</p>
        )}

        {brandName && brandName !== "-" && (
          <button
            type="button"
            className="product-brand-store-link"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate(`/store/${brandSlug}`, {
                state: {
                  brandName,
                  brandSlug,
                  brand: product.brand,
                },
              });
            }}
          >
            Visit the {brandName} Store
            <ExternalLink size={14} strokeWidth={1.9} />
          </button>
        )}

        <div className="pd-rating-row">
          <strong>{product.rating ? product.rating.toFixed(1) : "0"}</strong>

          <span className="pd-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={18}
                strokeWidth={1.7}
                fill={
                  star <= Math.round(product.rating || 0)
                    ? "currentColor"
                    : "none"
                }
              />
            ))}
          </span>

          <span className="pd-rating-count">
            {product.reviews || (product.reviewCount ? `${product.reviewCount} ratings` : "0 ratings")}
          </span>
        </div>

        {product.deal && (
          <div className="pd-pick">
            <BadgeCheck size={17} />
            <strong>{product.deal}</strong>
            {content.pickText && <span>{content.pickText}</span>}
          </div>
        )}

        <div className="pd-pricing">
          <div className="pd-price-line">
            {discount > 0 && <span className="pd-discount">-{discount}%</span>}

            <strong>
              <sup>₹</sup>
              {formatPrice(product.price)}
            </strong>
          </div>

          {product.mrp > product.price && (
            <div className="pd-mrp">
              M.R.P.: <del>₹{formatPrice(product.mrp)}</del>
            </div>
          )}

          <div className="pd-tax">Inclusive of all taxes</div>

          {saving > 0 && (
            <div className="pd-saving">You save ₹{formatPrice(saving)}</div>
          )}

          {product.voucherAmount && product.voucherAmount > 0 ? (
            <div
              style={{
                marginTop: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 14px",
                background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
                border: "1px solid #fdba74",
                borderRadius: "8px",
                color: "#c2410c",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <Gift size={18} />
              <span>
                Includes <strong>₹{formatPrice(product.voucherAmount)}</strong>{" "}
                Salar Shopping Voucher
                {product.voucherValidityDays
                  ? ` (${product.voucherValidityDays} Days Validity)`
                  : ""}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pd-divider" />

      {/* Available Offers Section */}
      <section className="pd-section pd-offers-section">
        <div className="pd-section-head pd-offers-head">
          <div>
            <span className="pd-eyebrow">EXTRA SAVINGS</span>
            <h2>Available offers</h2>
            <p>Best rewards and benefits for this referral purchase.</p>
          </div>

          <span className="pd-offer-count">
            {content.offers.length} offers
          </span>
        </div>

        <div className="pd-offers-scroll">
          <div className="pd-offers">
            {content.offers.map((offer, index) => (
              <article
                className="pd-offer-card"
                key={`${offer.title}-${index}`}
              >
                <div className="pd-offer-main">
                  <div className="pd-offer-icon">{offer.icon}</div>

                  <div className="pd-offer-copy">
                    <div className="pd-offer-title-row">
                      <strong>{offer.title}</strong>

                      <span className="pd-offer-tag">
                        {index === 0
                          ? "VOUCHER"
                          : index === 1
                          ? "PAYMENT"
                          : index === 2
                          ? "REFERRAL"
                          : "SAVE"}
                      </span>
                    </div>

                    <p>{offer.text}</p>
                  </div>
                </div>

                <button type="button" className="pd-offer-action">
                  {offer.action}
                  <ChevronRight size={14} strokeWidth={2} />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="pd-divider" />

      {/* Trust & Shopping Policies - Digital Oriented */}
      <section className="pd-section">
        <h2>Shopping policies</h2>

        <div className="pd-policies-scroll">
          <div className="pd-policies">
            <div className="pd-policy-item">
              <span className="pd-policy-icon">
                <Zap size={24} />
              </span>
              <strong>{content.policies.deliveryTitle || "Instant Delivery"}</strong>
              <span>{content.policies.delivery}</span>
            </div>

            <div className="pd-policy-item">
              <span className="pd-policy-icon">
                <Sparkles size={24} />
              </span>
              <strong>{content.policies.shippingTitle || "Zero Shipping Fee"}</strong>
              <span>{content.policies.shipping}</span>
            </div>

            <div
              className="pd-policy-item"
              title={content.policies.returns}
            >
              <span className="pd-policy-icon">
                <Undo2 size={24} />
              </span>
              <strong>
                {content.policies.returnsTitle || "Voucher Assurance"}
              </strong>
              <span>{content.policies.returns}</span>
            </div>

            <div className="pd-policy-item">
              <span className="pd-policy-icon">
                <WalletCards size={24} />
              </span>
              <strong>
                {content.policies.paymentTitle || "Pay Securely"}
              </strong>
              <span>{content.policies.payment}</span>
            </div>

            <div className="pd-policy-item">
              <span className="pd-policy-icon">
                <ShieldCheck size={24} />
              </span>
              <strong>
                {content.policies.securityTitle || "100% Authentic"}
              </strong>
              <span>{content.policies.security || "Protected checkout"}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="pd-divider" />

      {/* Product Details Accordion */}
      <section className="pd-section pd-details-section">
        <div className="pd-details-title">
          <div>
            <span className="pd-eyebrow">KNOW YOUR PRODUCT</span>
            <h2>Product Details</h2>
          </div>

          <Heart size={20} />
        </div>

        <ProductDetailAccordion sections={content.detailSections} />
      </section>
    </section>
  );
};

export default ProductDetailCenter;
